// Brand loaders, ported from the Claude Design project "Namibia Loading
// Animations" (Namibia Loaders.dc.html) — each is the Namibia outline plus
// one CSS keyframe (styles/motion.css), coloured by the portal tokens so it
// is right in both themes. Usage follows the design file's own notes:
//   02 Orbit     — "Default spinner"            → PageLoading / BrandLoader
//   01 Trace     — "App launch, splash"         → ScreenLoading (root guards)
//   03 Dune fill — "Uploads, booking submit"    → InlineLoader (buttons)
//   15 Business Owner — "dashboard, payouts"    → EarningsLoader
//   05 Shimmer   — "Skeleton sheen"             → Skeleton (CSS, no SVG)
// Under prefers-reduced-motion every keyframe runs once at 0s, exactly as
// the design file specifies, so the shape stays as a static mark.
import { useId, type ReactNode } from 'react';

/** The Namibia outline used by every loader in the set (viewBox 0 0 100 100). */
export const NAMIBIA_PATH =
  'M59.8 62.7L59.8 89L53.9 92.7L50.4 93.2L46.2 91.8L43.3 91.3L42.1 88.3L39.6 86.3L36.4 89.8L31.5 84.5L28.9 79.2L27.5 72.3L25.8 67.1L23.6 56.1L23.5 47.6L22.6 43.7L20.1 40.8L16.7 34.9L13.2 26.3L11.8 21.8L6.4 14.9L6 9.4L9.2 8L13.1 6.8L17.4 7L21.3 10.2L22.3 9.8L49.1 9.4L53.6 12.9L69.6 13.9L81.7 11L87.1 9.3L91.4 9.8L94 11.4L94 12L90.3 13.6L88.3 13.6L84.1 16.4L81.6 13.4L71.4 15.9L66.5 16.2L66.3 41.6L59.8 41.8L59.8 62.7Z';

/** The design file thickens the stroke as the mark shrinks (5 at 120px, 8 at
 * 32px, 11 at 20px) so the outline stays legible. */
function strokeFor(size: number): number {
  if (size >= 96) return 5;
  if (size >= 28) return 8;
  return 11;
}

/** 02 Orbit — a canyon segment circles the border over a faint outline. */
export function OrbitLoader({ size = 40, className = '' }: { size?: number; className?: string }) {
  const sw = strokeFor(size);
  return (
    <svg className={`ng-loader ${className}`} width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      <path d={NAMIBIA_PATH} fill="none" stroke="var(--border)" strokeWidth={sw} strokeLinejoin="round" />
      <path
        d={NAMIBIA_PATH}
        pathLength={100}
        fill="none"
        stroke="var(--canyon)"
        strokeWidth={sw}
        strokeLinejoin="round"
        strokeLinecap="round"
        strokeDasharray="22 78"
        className="nm-orbit"
      />
    </svg>
  );
}

/** 01 Trace — the border draws on, then erases. */
export function TraceLoader({ size = 64, className = '' }: { size?: number; className?: string }) {
  const sw = strokeFor(size);
  return (
    <svg className={`ng-loader ${className}`} width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      <path
        d={NAMIBIA_PATH}
        pathLength={100}
        fill="none"
        stroke="var(--canyon)"
        strokeWidth={sw}
        strokeLinejoin="round"
        strokeLinecap="round"
        strokeDasharray={100}
        className="nm-trace"
      />
    </svg>
  );
}

/** 03 Dune fill — sand rises and drains inside the outline. */
export function DuneFillLoader({ size = 20, className = '' }: { size?: number; className?: string }) {
  const clipId = useId();
  const big = size >= 96;
  return (
    <svg className={`ng-loader ${className}`} width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      <defs>
        <clipPath id={clipId}>
          <path d={NAMIBIA_PATH} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <rect x="0" y="0" width="100" height="100" fill="var(--tint-sand)" />
        <rect x="0" y="0" width="100" height="100" fill="var(--canyon)" className="nm-fill" />
      </g>
      {big ? <path d={NAMIBIA_PATH} fill="none" stroke="var(--canyon)" strokeWidth={2.5} strokeLinejoin="round" /> : null}
    </svg>
  );
}

/** 15 Business Owner — earnings bars rise in turn. */
export function EarningsLoader({ size = 48, className = '' }: { size?: number; className?: string }) {
  return (
    <svg className={`ng-loader ${className}`} width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      <rect x="26" y="48" width="12" height="30" rx="3" fill="var(--sage)" className="nm-grow" />
      <rect x="44" y="34" width="12" height="44" rx="3" fill="var(--desert)" className="nm-grow" style={{ animationDelay: '.2s' }} />
      <rect x="62" y="22" width="12" height="56" rx="3" fill="var(--canyon)" className="nm-grow" style={{ animationDelay: '.4s' }} />
      <path d="M20 80L80 80" stroke="var(--desert)" strokeWidth={2.5} strokeLinecap="round" />
    </svg>
  );
}

/** The default mark: Orbit. Kept under the old name for existing callers. */
export const BrandLoader = OrbitLoader;

/** A centred loader with an (optional) visible label — the drop-in for the
 * old `<p className="text-body text-ink-2">Loading…</p>`. */
export function PageLoading({
  label = 'Loading…',
  compact = false,
  testId,
}: {
  label?: string;
  compact?: boolean;
  testId?: string;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={testId}
      className={`flex flex-col items-center justify-center gap-sm text-center ${compact ? 'py-md' : 'py-xxl'}`}
    >
      <OrbitLoader size={compact ? 32 : 56} />
      <p className="text-small text-ink-2">{label}</p>
    </div>
  );
}

/** Full-viewport variant for the root guards (session bootstrap): Trace,
 * the design file's launch/splash loader. */
export function ScreenLoading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-background" role="status" aria-live="polite">
      <div className="flex flex-col items-center gap-md">
        <TraceLoader size={96} />
        <p className="text-small text-ink-2">{label}</p>
      </div>
    </div>
  );
}

/** Inline (button-sized) loader for submitting states: Dune fill. */
export function InlineLoader({ className = '' }: { className?: string }) {
  return <DuneFillLoader size={18} className={`inline-block align-[-3px] ${className}`} />;
}

// ---- Skeletons (05 Shimmer, as CSS) ----------------------------------------

export function Skeleton({ className = '' }: { className?: string }) {
  return <span aria-hidden="true" className={`ng-skeleton block ${className}`} />;
}

/** Card-shaped skeleton list: `rows` lines of varying width. */
export function SkeletonRows({ rows = 4, label = 'Loading…' }: { rows?: number; label?: string }) {
  return (
    <div role="status" aria-live="polite" className="rounded-card border border-border bg-surface p-lg shadow-sm">
      <span className="sr-only">{label}</span>
      <div className="flex flex-col gap-md">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-md">
            <Skeleton className="h-4 w-[22%]" />
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-5 w-[14%] rounded-chip" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Stat-tile skeleton grid for dashboards, with the Business Owner bars in
 * the first tile so the wait says what is coming. */
export function SkeletonTiles({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 gap-md sm:grid-cols-2 lg:grid-cols-4" role="status" aria-live="polite">
      <span className="sr-only">Loading…</span>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="relative rounded-card border border-border bg-surface p-md shadow-sm" aria-hidden="true">
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="mt-md h-7 w-2/3" />
          {i === 0 ? <EarningsLoader size={44} className="absolute right-md top-sm" /> : null}
        </div>
      ))}
    </div>
  );
}

/** Wraps a query's three states: skeleton while loading, an error strip, or
 * the children. Keeps route files free of the same ternary. */
export function QueryState({
  isLoading,
  error,
  loading,
  errorPrefix = "Couldn't load this",
  onRetry,
  children,
}: {
  isLoading: boolean;
  error?: unknown;
  loading?: ReactNode;
  errorPrefix?: string;
  onRetry?: () => void;
  children: ReactNode;
}) {
  if (isLoading) return <>{loading ?? <PageLoading />}</>;
  if (error) {
    const message = error instanceof Error ? error.message : String(error);
    return (
      <div role="alert" className="flex flex-wrap items-center gap-md rounded-card border border-danger bg-surface px-lg py-md text-body text-ink">
        <span className="h-2 w-2 rounded-full bg-danger" aria-hidden="true" />
        <span className="flex-1">
          {errorPrefix}: {message}
        </span>
        {onRetry ? (
          <button type="button" onClick={onRetry} className="text-body font-semibold text-canyon underline">
            Try again
          </button>
        ) : null}
      </div>
    );
  }
  return <>{children}</>;
}
